import { type Notification } from "@app/shared/atoms/notifications-atom";
import { getNotificationAppearance } from "@app/shared/utils/notification-appearance";
import { getNotificationContent } from "@app/shared/utils/notification-content";
import { formatRelativeTime } from "@core/shared/utils";
import { cn } from "@design-system/lib/utils";
import { trans, plainTrans } from "@mongez/localization";
import { Trash2 } from "lucide-react";

type NotificationListItemProps = {
  compact?: boolean;
  notification: Notification;
  onDelete: (notification: Notification) => void;
  onOpen: (notification: Notification) => void;
};

export function NotificationListItem({
  compact = false,
  notification,
  onDelete,
  onOpen,
}: NotificationListItemProps) {
  const content = getNotificationContent(notification);
  const appearance = getNotificationAppearance(notification.type);
  const { Icon } = appearance;

  return (
    <article
      className={cn(
        "group relative flex items-start gap-3 rounded-2xl border transition duration-200",
        compact ? "p-3" : "p-4",
        notification.isRead
          ? "border-transparent bg-white hover:border-slate-200 hover:bg-slate-50 dark:bg-slate-950 dark:hover:border-slate-700 dark:hover:bg-slate-900"
          : "border-primary/15 bg-primary/[0.045] shadow-sm hover:border-primary/25 hover:bg-primary/[0.075] dark:bg-primary/10",
      )}
    >
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-xl",
          compact ? "h-10 w-10" : "h-11 w-11",
          appearance.iconClassName,
        )}
      >
        <Icon className={compact ? "h-5 w-5" : "h-5.5 w-5.5"} />
      </span>

      <button
        type="button"
        onClick={() => onOpen(notification)}
        className="min-w-0 flex-1 pe-8 text-start"
      >
        <span className="flex items-start justify-between gap-2">
          <span
            className={cn(
              "line-clamp-1 text-sm text-slate-900 dark:text-white",
              notification.isRead ? "font-bold" : "font-black",
            )}
          >
            {content.title}
          </span>
          {!notification.isRead && (
            <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-primary ring-4 ring-primary/10" />
          )}
        </span>

        {content.message && (
          <span className="mt-1 line-clamp-2 block text-xs leading-5 text-slate-600 dark:text-slate-300">
            {content.message}
          </span>
        )}
        <span className="mt-1.5 block text-[11px] font-medium text-slate-400 dark:text-slate-500">
          {formatRelativeTime(notification.createdAt)}
        </span>
      </button>

      <button
        type="button"
        title={plainTrans("delete")}
        aria-label={plainTrans("delete")}
        onClick={() => onDelete(notification)}
        className="absolute bottom-2 end-2 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 opacity-0 transition hover:bg-red-50 hover:text-red-500 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-red-950/50 max-md:opacity-100"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </article>
  );
}
